<template>
  <div class="card">
    <div class="row">
      <div class="col-md-8 d-flex flex-column justify-content-center">
        <div class="row">
          <div class="col-sm-6 mt-3 mb-3">
            <div class="d-flex flex-column align-items-center justify-content-center">
              <div class="d-flex align-items-center">
                <i class="fa fa-check font-xl text-success mr-3"></i>
                <div class="number-big">
                  <v-animated-number
                    :value="sessionsReviewed"
                    :formatValue="(value) => (value.toFixed(0))"
                    :duration="500"
                  />
                </div>
              </div>
              <span class="text-muted text-sm">{{ $t('word.reviewed') }}</span>
            </div>
          </div>
          <div class="col-sm-6 mt-3 mb-3">
            <div
              class="d-flex flex-column align-items-center justify-content-center"
            >
              <div class="d-flex align-items-center">
                <i class="far fa-clock font-xl text-danger mr-3"></i>
                <div class="number-big">
                  <v-animated-number
                    :value="averageDurationMinutes"
                    :formatValue="(value) => (value.toFixed(0))"
                    :duration="500"
                  />:<span v-if="averageDurationSeconds < 10">0</span>
                  <v-animated-number
                    :value="averageDurationSeconds"
                    :formatValue="(value) => (value.toFixed(0))"
                    :duration="500"
                  />
                </div>
              </div>
              <span class="text-muted text-sm">{{ $t('phrase.time_in_research') }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-11">
            <hr/>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 mt-3 mb-3">
            <div
              class="d-flex align-items-center justify-content-center"
              style="height: 100px"
            >
              <v-loader v-if="loading"/>
              <v-gender-doughnut-chart
                :key="genders.length"
                :genders="genders"
                v-else-if="genders.length > 0"
              />
              <div v-else class="text-muted">
                {{ $t('phrase.no_data_available') }}
              </div>
            </div>
          </div>
          <div class="col-sm-6 mt-3 mb-3">
            <div
              class="d-flex align-items-center justify-content-center"
              style="height: 100px"
            >
              <v-loader v-if="loading"/>
              <v-age-doughnut-chart
                :key="ages.length"
                :ages="ages"
                v-else-if="ages.length > 0"
              />
              <div v-else class="text-muted">
                {{ $t('phrase.no_data_available') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-sm-4 d-flex justify-content-center align-items-center"
        style="height: 300px"
      >
        <v-loader v-if="loading"/>
        <v-geo-chart
          v-else
          color="#8897AA"
          :values="provinceCounts"
          :max-value="provinceMaxValue"
          :height="300"
          :country-code="research.country"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AnimatedNumber from 'animated-number-vue';
import AgeDoughnutChart from '@/organisation/components/dashboard/AgeDoughnutChart.vue';
import GenderDoughnutChart from '@/organisation/components/dashboard/GenderDoughnutChart.vue';
import { stringToPrettyDate } from '@/common/utils/formatting';
import GeoChart from '@/organisation/components/research/widgets/GeoChart.vue';
import { PROVINCES } from '@/organisation/constants';
import Loader from '@/common/components/Loader.vue';
import { mapGetters } from 'vuex';
import { sum } from '@/common/utils/math';

export default {
  name: 'ResearchStatistics',
  components: {
    'v-age-doughnut-chart': AgeDoughnutChart,
    'v-loader': Loader,
    'v-animated-number': AnimatedNumber,
    'v-gender-doughnut-chart': GenderDoughnutChart,
    'v-geo-chart': GeoChart,
  },
  data() {
    return {
      provinceCounts: null,
    };
  },
  computed: {
    ...mapGetters('researches', [
      'research',
      'data',
      'loadingResearch',
      'loadingResearchReviewSessions',
    ]),
    loading() {
      return this.loadingResearchReviewSessions || this.loadingResearch;
    },
    averageDuration() {
      if (this.data.sessions === null || this.data.sessions.length === 0) {
        return 0;
      }
      return sum(this.data.sessions
        .filter((session) => {
          // Filter all sessions that are not reviewed within 20 minutes;
          const opened = new Date(session.opened);
          const reviewed = new Date(session.reviewed);
          return (reviewed - opened) / 1000 < 1200;
        })
        .map((session) => {
          const opened = new Date(session.opened);
          const reviewed = new Date(session.reviewed);
          return (reviewed - opened) / 1000;
        })) / this.data.sessions.length;
    },
    sessionsReviewed() {
      if (this.data.sessions) {
        return this.data.sessions.length;
      }
      return 0;
    },
    genders() {
      if (!this.data.sessions) {
        return [];
      }
      return this.data.sessions.map((session) => (session.gender));
    },
    ages() {
      if (!this.data.sessions) {
        return [];
      }
      const refYear = new Date(this.research.start_date).getFullYear();
      return this.data.sessions
        .filter((session) => (session.birth_year))
        .map((session) => (refYear - session.birth_year));
    },
    provinces() {
      if (!this.data.sessions) {
        return [];
      }
      return this.data.sessions
        .filter((session) => (session.province))
        .map((session) => (session.province));
    },
    averageDurationSeconds() {
      return this.averageDuration - (this.averageDurationMinutes * 60);
    },
    averageDurationMinutes() {
      return Math.floor(this.averageDuration / 60);
    },
    provinceMaxValue() {
      const maxValue = Math.max(...this.provinceCounts.map((province) => (province[1])));
      if (maxValue < 10) {
        return 10;
      }
      return Math.ceil(maxValue / 10) * 10;
    },
  },
  watch: {
    provinces() {
      this.calculateProvinceCounts();
    },
  },
  methods: {
    stringToPrettyDate,
    calculateProvinceCounts() {
      const provinceCount = {};
      let provinceCode;
      this.provinces.forEach((province) => {
        provinceCode = PROVINCES[this.research.country][province];
        if (provinceCode in provinceCount) {
          provinceCount[provinceCode] += 1;
        } else {
          provinceCount[provinceCode] = 1;
        }
      });
      this.provinceCounts = Object.entries(provinceCount);
    },
  },
  created() {
    this.calculateProvinceCounts();
  },
};
</script>

<style scoped>
.number-big {
  font-size: 1.5rem !important;
}

.text-sm {
  font-size: 0.75rem !important;
}

.font-xl {
  font-size: 1.5rem !important;
}
</style>
